import {useRef, useEffect} from "react";
import {Slider, type SliderHandle, Input, XStack, DashedBadge, CarNumberInput} from "@chayka/ui";
import {ChevronBack, ChevronForward} from "@chayka/icons";
import {useAuth} from "@/hooks/useAuth";

type TParkingSpot = {
  id: number;
  name: string;
  price: number;
  nightHoursTogether: boolean;
};

type ParkingSliderProps = {
  parkingSpots: TParkingSpot[];
  selectedParkingSpot: TParkingSpot | null;
  onParkingSpotChange: (parkingSpot: TParkingSpot | null) => void;
  carNumber: string;
  setCarNumber: (carNumber: string) => void;
  carModel: string;
  setCarModel: (carModel: string) => void;
};

export const ParkingSlider: React.FC<ParkingSliderProps> = ({
  parkingSpots,
  selectedParkingSpot,
  onParkingSpotChange,
  carNumber,
  setCarNumber,
  carModel,
  setCarModel,
}) => {
  const sliderRef = useRef<SliderHandle>(null);
  const lastSeededCarNumberRef = useRef<string | null>(null);
  const lastSeededCarModelRef = useRef<string | null>(null);

  const {user} = useAuth();
  const defaultCarNumber = user?.carNumber;
  const defaultCarModel = user?.carModel;

  useEffect(() => {
    if (!defaultCarNumber || carNumber || lastSeededCarNumberRef.current === defaultCarNumber) {
      return;
    }

    setCarNumber(defaultCarNumber);
    lastSeededCarNumberRef.current = defaultCarNumber;
  }, [carNumber, defaultCarNumber, setCarNumber]);

  useEffect(() => {
    if (!defaultCarModel || carModel || lastSeededCarModelRef.current === defaultCarModel) {
      return;
    }

    setCarModel(defaultCarModel);
    lastSeededCarModelRef.current = defaultCarModel;
  }, [carModel, defaultCarModel, setCarModel]);

  return (
    <div className="relative rounded-xl border border-stroke bg-background px-4 py-3 text-center gap-2 flex flex-col items-stretch">
      <div
        className="absolute left-2 top-1/2 -translate-y-1/2 z-3"
        onClick={() => sliderRef.current?.navigate(-1)}
      >
        <ChevronBack size={20} />
      </div>
      <div
        className="absolute right-2 top-1/2 -translate-y-1/2 z-3"
        onClick={() => sliderRef.current?.navigate(1)}
      >
        <ChevronForward size={20} />
      </div>
      <XStack className="absolute w-full left-0 top-10 px-10 py-3 gap-2 z-2">
        <Input
          value={carModel}
          onChange={(e) => setCarModel(e.currentTarget.value)}
          placeholder="Марка машины"
          className="flex-1 h-7 text-sm px-2!"
        />
        <CarNumberInput
          value={carNumber}
          onChange={(e) => setCarNumber(e.currentTarget.value)}
          placeholder="Номер машины"
          className="flex-1 h-7 text-sm px-2!"
        />
      </XStack>
      <Slider
        ref={sliderRef}
        withDots
        items={parkingSpots}
        viewportClassName="px-1 -mx-6"
        panelClassName="px-1 mx-4"
        activeIndex={parkingSpots.findIndex((p) => p.id === selectedParkingSpot?.id)}
        onIndexChange={(index) => onParkingSpotChange(parkingSpots[index] ?? null)}
        renderItem={({item}) => <DashedBadge className="mx-6 mb-10">{item.name}</DashedBadge>}
      />
    </div>
  );
};
